import { CommonModule } from "@angular/common";
import { Component, HostBinding, Input, inject, signal } from "@angular/core";
import { Router } from "@angular/router";
import { PhmsReturnContext, readReturnContext } from "./phms-detail-navigation";
import { PhmsDetailNavBarService } from "./phms-detail-nav-bar.service";

/**
 * Common navigation strip for PHMS detail screens. It reads the preserved
 * return context supplied by PhmsDetailNavigationService and provides a compact
 * route back to the dashboard, list, or work surface that opened the detail.
 */
@Component({
  selector: "phms-detail-nav-bar",
  standalone: true,
  imports: [CommonModule],
  template: `
    <nav
      *ngIf="isVisible"
      class="phms-detail-nav-bar"
      aria-label="Detail navigation"
      data-testid="detail-nav-bar"
    >
      <div class="phms-detail-nav-bar__start">
        <button
          *ngIf="returnTarget as target"
          type="button"
          class="phms-detail-nav-bar__item phms-detail-nav-bar__back"
          data-testid="return-bar"
          (click)="goBack()"
        >
          <i class="pi pi-arrow-left" aria-hidden="true"></i>
          <span>Back to {{ target.label }}</span>
        </button>

        <div *ngIf="effectivePageLabel" class="phms-detail-nav-bar__label">
          <span class="phms-detail-nav-bar__title">{{ effectivePageLabel }}</span>
          <span *ngIf="effectiveContextLabel" class="phms-detail-nav-bar__context">{{ effectiveContextLabel }}</span>
        </div>
      </div>

      <div class="phms-detail-nav-bar__actions">
        <ng-content select="[detail-nav-actions]"></ng-content>
      </div>
    </nav>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
        color: var(--phms-detail-nav-text-color, var(--custom-common-header-text));
      }

      .phms-detail-nav-bar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: calc(var(--custom-element-spacing, 8) * 1px);
        box-sizing: border-box;
        min-height: var(--phms-detail-nav-min-height, 2.125rem);
        padding: 0 var(--phms-detail-nav-horizontal-padding, 0.75rem);
        background: var(--phms-detail-nav-background, var(--custom-common-header-background-color));
        border-top: var(--phms-detail-nav-top-border-width, 1px) solid
          var(--phms-detail-nav-top-border-color, rgba(255, 255, 255, 0.12));
        border-bottom: var(--phms-detail-nav-border-width, 1px) solid
          var(--phms-detail-nav-border-color, var(--custom-border-strong-color));
        box-shadow: var(--phms-detail-nav-shadow, var(--custom-shadow-elevation-1));
      }

      .phms-detail-nav-bar__start,
      .phms-detail-nav-bar__actions {
        display: flex;
        align-items: center;
        gap: calc(var(--custom-element-spacing, 8) * 1px);
        min-width: 0;
      }

      .phms-detail-nav-bar__start {
        flex: 1 1 auto;
      }

      .phms-detail-nav-bar__actions {
        flex: 0 0 auto;
        justify-content: flex-end;
      }

      .phms-detail-nav-bar__back {
        color: var(--phms-detail-nav-action-color, var(--custom-common-header-text));
      }

      .phms-detail-nav-bar__item {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
        min-width: 0;
        background: transparent;
        border: none;
        border-radius: var(--phms-detail-nav-item-radius, 4px);
        padding: 0.25rem 0.55rem;
        cursor: pointer;
        font: inherit;
        font-size: var(--phms-detail-nav-font-size, 0.82rem);
        font-weight: 600;
        line-height: 1.25;
        white-space: nowrap;
      }

      .phms-detail-nav-bar__item:hover {
        background: var(--phms-detail-nav-item-hover-background, rgba(255, 255, 255, 0.12));
      }

      .phms-detail-nav-bar__item:focus-visible {
        outline: 2px solid var(--phms-detail-nav-focus-color, var(--custom-focus-ring-color));
        outline-offset: -2px;
      }

      .phms-detail-nav-bar__label {
        display: inline-flex;
        align-items: baseline;
        gap: 0.45rem;
        min-width: 0;
        padding-left: calc(var(--custom-element-spacing, 8) * 1px);
        border-left: 1px solid var(--phms-detail-nav-separator-color, rgba(255, 255, 255, 0.24));
      }

      .phms-detail-nav-bar__title {
        overflow: hidden;
        color: var(--phms-detail-nav-title-color, var(--custom-common-header-text));
        font-size: 0.86rem;
        font-weight: 700;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .phms-detail-nav-bar__context {
        overflow: hidden;
        color: var(--phms-detail-nav-context-color, var(--custom-common-header-text));
        font-size: 0.78rem;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      @media (max-width: 600px) {
        .phms-detail-nav-bar__label {
          display: none;
        }

        .phms-detail-nav-bar__back {
          overflow: hidden;
        }

        .phms-detail-nav-bar__back span {
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    `,
  ],
})
export class PhmsDetailNavBarComponent {
  private readonly router = inject(Router);
  private readonly detailNav = inject(PhmsDetailNavBarService);

  @Input() pageLabel = "";
  @Input() contextLabel = "";
  @Input() alwaysVisible = false;
  @Input() fallbackUrl = "/phms/dashboard";
  @Input() fallbackLabel = "Dashboard";
  @Input() showFallback = true;
  @Input() useSharedState = false;

  readonly returnContext = signal<PhmsReturnContext | null>(readReturnContext(readHistoryState()));

  private get sharedState() {
    return this.detailNav.state();
  }

  get effectivePageLabel(): string {
    return this.useSharedState ? this.sharedState.pageLabel : this.pageLabel;
  }

  get effectiveContextLabel(): string {
    return this.useSharedState ? this.sharedState.contextLabel : this.contextLabel;
  }

  get effectiveFallbackUrl(): string {
    return this.useSharedState ? this.sharedState.fallbackUrl : this.fallbackUrl;
  }

  get effectiveFallbackLabel(): string {
    return this.useSharedState ? this.sharedState.fallbackLabel : this.fallbackLabel;
  }

  get effectiveShowFallback(): boolean {
    return this.useSharedState ? this.sharedState.showFallback : this.showFallback;
  }

  get effectiveReturnContext(): PhmsReturnContext | null {
    return this.useSharedState ? this.sharedState.returnContext : this.returnContext();
  }

  get returnTarget(): PhmsReturnContext | null {
    const ctx = this.effectiveReturnContext;
    if (ctx) {
      return ctx;
    }
    return this.effectiveShowFallback && this.effectiveFallbackUrl
      ? { url: this.effectiveFallbackUrl, label: this.effectiveFallbackLabel || "Dashboard" }
      : null;
  }

  get isVisible(): boolean {
    if (this.useSharedState && !this.sharedState.enabled) {
      return false;
    }

    return this.alwaysVisible || this.returnTarget !== null || Boolean(this.effectivePageLabel || this.effectiveContextLabel);
  }

  @HostBinding("style.display")
  get hostDisplay(): "block" | "none" {
    return this.isVisible ? "block" : "none";
  }

  goBack(): void {
    const target = this.returnTarget;
    if (target) {
      void this.router.navigateByUrl(target.url);
    }
  }
}

function readHistoryState(): unknown {
  return typeof history !== "undefined" ? history.state : null;
}
